import { useEffect, useState } from 'react';
import axios from 'axios';
import { useVenue } from '../contexts/VenueContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared "is there something the owner must still do?" signal.
 *
 * Two drop-dead-simple states we want surfaced consistently across pages
 * (Scan page banner, Analytics banner, bottom-nav red dots):
 *   - `needsSetup`  → venue is in `setup_mode` (checklist unfinished)
 *   - `needsPayout` → venue was approved but is still hidden from guests
 *                      because no payout method is connected (`is_on_hold`)
 *
 * Payout status is only fetched for `approved` venues — setup_mode venues
 * don't connect a payout yet, and `live` venues already have one, so this
 * avoids a wasted request on every tab render for the common case.
 */
export function useVenueActionNeeded() {
  const { activeVenueAppId, activeVenue } = useVenue();
  const [payoutStatus, setPayoutStatus] = useState(null);

  useEffect(() => {
    if (!activeVenueAppId || activeVenue?.status !== 'approved') {
      setPayoutStatus(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/venue/payout-method/${activeVenueAppId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPayoutStatus(res.data || null);
      } catch {
        if (!cancelled) setPayoutStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeVenueAppId, activeVenue?.status]);

  const needsSetup = activeVenue?.status === 'setup_mode';
  const needsPayout = activeVenue?.status === 'approved' && payoutStatus?.is_on_hold === true;

  return { needsSetup, needsPayout, needsAction: needsSetup || needsPayout };
}
